import React, { useState } from 'react';
import { Heart, Minus, Plus, Bell } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import ProductDetail from '../ProductDetail';
import { hasDiscount, priceBefore } from '../../utils/discount';
import { formatPrice } from '../../utils/price';
import CareBadge from '../CareBadge/CareBadge';

export default function ProductCard({ product }) {
  const { addToCart, removeFromCart, updateCartQuantity, cart, toggleFavorite, isFavorite, toggleNotification, isNotified } = useApp();
  const [imgError, setImgError] = useState(false);
  const [showDetail, setShowDetail] = useState(false);
  const [notifError, setNotifError] = useState('');

  // Счётчик на карточке — только для товара без размеров; для размерных
  // всегда «Выбрать размер» (variant_id учитывается в ProductDetail)
  const inCart = cart.find(item => item.id === product.id && !item.variant_id);
  const quantity = inCart ? inCart.quantity : 0;
  const fav = isFavorite(product.id);
  const notified = isNotified(product.id);
  const isOutOfStock = !product.in_stock || product.quantity <= 0;
  const hasVariants = (product.variants || []).length > 0;

  const handleNotification = async (e) => {
    e.stopPropagation();
    setNotifError('');
    const result = await toggleNotification(product);
    if (!result.success) {
      setNotifError(result.error);
      setTimeout(() => setNotifError(''), 3000);
    }
  };

  const handleFavorite = (e) => {
    e.stopPropagation();
    toggleFavorite(product);
  };

  const handleAdd = (e) => {
    e.stopPropagation();
    if (isOutOfStock) return;
    if (hasVariants) {
      setShowDetail(true);
      return;
    }
    addToCart(product, 1);
  };

  const handleRemove = (e) => {
    e.stopPropagation();
    if (quantity <= 1) {
      removeFromCart(product.id);
    } else {
      updateCartQuantity(product.id, quantity - 1);
    }
  };

  return (
    <>
      <div
        onClick={() => setShowDetail(true)}
        className="group relative flex h-full flex-col rounded-2xl bg-cream-card p-3 shadow-sm ring-1 ring-border-warm transition hover:shadow-md cursor-pointer"
      >
        <button
          onClick={handleFavorite}
          aria-label="В избранное"
          className={`absolute right-2 top-2 z-10 grid h-9 w-9 place-items-center rounded-full bg-white/90 backdrop-blur transition active:scale-90 ${
            fav ? 'text-rose-500' : 'text-[#6F7C72] hover:text-rose-500'
          }`}
        >
          <Heart className={`h-5 w-5 transition ${fav ? 'fill-rose-500' : ''}`} />
        </button>

        <div className="relative mb-3 flex aspect-square items-center justify-center rounded-xl bg-cream overflow-hidden">
          {product.image_url && !imgError ? (
            <img 
              src={product.image_url} 
              alt={product.name}
              className="h-full w-full object-cover"
              onError={() => setImgError(true)}
            />
          ) : (
            <span className="text-6xl drop-shadow-sm">🌱</span>
          )}
        </div>

      <h3 className="mt-1 line-clamp-2 min-h-[2.5rem] text-sm font-medium text-[#2F3A31]">
        {product.name}
      </h3>

      {/* Фиксированная строка под бейдж ухода — без неё цена в карточке без
          уровня ухода поднималась вверх и «плыла» относительно соседок в ряду */}
      <div className="mt-1.5 flex h-[22px] items-start">
        <CareBadge careLevel={product.care_level} />
      </div>

      {/* Блок цены: grid с фиксированной первой строкой — цена всегда на одном
          уровне во всех карточках, со скидкой и без */}
      <div className="mt-2 grid min-h-[2.5rem] grid-rows-[1.375rem_1fr] items-end">
        {isOutOfStock ? (
          <div className="col-span-2 self-end text-sm font-semibold text-[#6F7C72]">Нет в наличии</div>
        ) : (
          <>
            <div className="flex items-center gap-1.5 overflow-hidden">
              {hasDiscount(product) ? (
                <>
                  <span className="truncate text-xs text-[#6F7C72] line-through">
                    {formatPrice(priceBefore(product.price, product.discount_percent))} ₽
                  </span>
                  <span className="shrink-0 rounded-full bg-[#D64545]/15 px-1.5 py-0.5 text-[10px] font-semibold leading-none text-[#D64545]">
                    −{product.discount_percent}%
                  </span>
                  {Number(product.discount_percent) === 50 && (
                    <span className="shrink-0 text-xs leading-none" aria-label="Распродажа" title="Распродажа">🔥</span>
                  )}
                </>
              ) : null}
            </div>
            <div className="flex items-center gap-1.5">
              <span className="text-base font-bold text-[#2F3A31]">
                {hasVariants ? `от ${formatPrice(product.price)}` : formatPrice(product.price)} ₽
              </span>
            </div>
          </>
        )}
      </div>

      <div className="mt-auto pt-3">
        {notifError && (
          <div className="mb-2 rounded-lg bg-[#D64545]/10 px-2 py-1 text-[11px] text-[#D64545] text-center">
            {notifError}
          </div>
        )}
        {isOutOfStock ? (
          <button
            onClick={handleNotification}
            className={`flex w-full items-center justify-center gap-1.5 rounded-xl py-2 text-sm font-semibold transition active:scale-[0.98] ${
              notified
                ? 'bg-green-sage/20 text-green-primary ring-1 ring-green-sage'
                : 'bg-yellow-soft/20 text-terracotta ring-1 ring-yellow-soft hover:bg-yellow-soft/30'
            }`}
          >
            <Bell className={`h-4 w-4 ${notified ? 'fill-green-primary' : ''}`} />
            <span>{notified ? 'Уведомим' : 'Сообщить'}</span>
          </button>
        ) : hasVariants ? (
          <button
            onClick={() => setShowDetail(true)}
            className="w-full rounded-xl bg-terracotta py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-terracotta/90 active:scale-[0.98]"
          >
            Выбрать размер
          </button>
        ) : quantity === 0 ? (
          <button
            onClick={handleAdd}
            className="w-full rounded-xl bg-terracotta py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-terracotta/90 active:scale-[0.98]"
          >
            В корзину
          </button>
        ) : (
          <div className="flex items-center justify-between rounded-xl bg-green-sage/20 p-1 overflow-hidden">
            <button
              onClick={handleRemove}
              className="grid h-8 w-8 place-items-center rounded-lg bg-white text-green-primary shadow-sm active:scale-90"
              aria-label="Убрать"
            >
              <Minus className="h-4 w-4" />
            </button>
            <span className="text-sm font-bold text-green-primary">{quantity}</span>
            <button
              onClick={handleAdd}
              className="grid h-8 w-8 place-items-center rounded-lg bg-green-primary text-white shadow-sm active:scale-90"
              aria-label="Добавить"
            >
              <Plus className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>
      </div>

      {showDetail && (
        <ProductDetail product={product} onClose={() => setShowDetail(false)} />
      )}

    </>
  );
}
